import React, { useState } from 'react';
import DeliveryHistory from "./DeliveryHistory";
import { EnhancedDeliverySchedule } from '../../hooks/useSubscriptionData';
import { SubscriptionState } from '../../types';
import { useTranslation } from 'react-i18next';

interface DeliveryHistorySectionProps {
  upcomingDeliveries: EnhancedDeliverySchedule[];
  pastDeliveries: EnhancedDeliverySchedule[];
  activeTab: 'upcoming' | 'past';
  onChangeTab: (tab: 'upcoming' | 'past') => void;
  onSkipDelivery: (deliveryId: string, date: string) => void;
  subscription: SubscriptionState;
  processingDeliveryId: string | null;
  loading: boolean;
}

const ITEMS_PER_PAGE = 6;

const DeliveryHistorySection: React.FC<DeliveryHistorySectionProps> = ({
  upcomingDeliveries,
  pastDeliveries,
  activeTab,
  onChangeTab,
  onSkipDelivery,
  subscription,
  processingDeliveryId,
  loading
}) => {
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [pastPage, setPastPage] = useState(1);

  const { t } = useTranslation();

  const handleShowMore = () => {
    if (activeTab === 'upcoming') {
      setUpcomingPage(prev => prev + 1);
    } else {
      setPastPage(prev => prev + 1);
    }
  };

  // Filter deliveries for the selected subscription only
  const filteredUpcomingDeliveries = upcomingDeliveries.filter(
    delivery => delivery.subscriptionId === subscription.id
  );
  
  const filteredPastDeliveries = pastDeliveries.filter(
    delivery => delivery.subscriptionId === subscription.id
  );

  const currentDeliveries = activeTab === 'upcoming' ? filteredUpcomingDeliveries : filteredPastDeliveries;
  const currentPage = activeTab === 'upcoming' ? upcomingPage : pastPage;
  const paginatedDeliveries = currentDeliveries.slice(0, currentPage * ITEMS_PER_PAGE);
  const hasMore = paginatedDeliveries.length < currentDeliveries.length;

  return (
    <div className="bg-white rounded-t-3xl pt-5 px-4 mb-5">
      <h2 className="font-semibold mb-2">{t('subscription.subscriptionDetails.shipmentHistory')}</h2>
      
      {/* Tabs */}
      <div className="flex mb-4 border-b">
        <button
          className={`p-3 ${
            activeTab === 'upcoming' 
              ? 'font-medium w-1/2 bg-white border-none underline underline-offset-[14px] decoration-amber-300 decoration-4'
              : 'text-gray-300 w-1/2 bg-white border-none'
          }`}
          onClick={() => onChangeTab('upcoming')}
        >
          {t('subscription.subscriptionDetails.tabs.upcomingDeliveries')}
        </button>
        <button
          className={`p-3 ${
            activeTab === 'past'
              ? 'font-medium w-1/2 bg-white border-none underline underline-offset-[14px] decoration-amber-300 decoration-4'
              : 'text-gray-300 w-1/2 bg-white border-none'
          }`}
          onClick={() => onChangeTab('past')}
        >
          {t('subscription.subscriptionDetails.tabs.pastDeliveries')}
        </button>
      </div>
      
      {/* Content */}
      <DeliveryHistory
        deliveries={paginatedDeliveries}
        onSkipDelivery={onSkipDelivery}
        subscription={subscription}
        processingDeliveryId={processingDeliveryId}
        loading={loading}
        isPastView={activeTab === 'past'}
      />

      {/* Show More Button */}
      {hasMore && (
        <div className="flex justify-center mb-4 py-3">
          <button
            onClick={handleShowMore}
            className="text-gray-400 font-medium text-sm border-none bg-white"
          >
            Show More
          </button>
        </div>
      )}
    </div>
  );
};

export default DeliveryHistorySection; 